import {
  live,
  type AgendaInput,
  type AgendaItem,
  type Booking,
  type Mode,
  type Plan,
  type PlanStop,
  type Point,
  type WaySaid,
} from '@trippath/shared';

import { wallOf } from '../../lib/clock';
import { plusDays } from '../../lib/countryFacts';
import { wayTimes } from '../../components/way/WayDetail';

/** A place of a day: where it is, and what it is called. */
export type DayPlace = Point & { name: string };

/** What a day at a stop holds, in its order: where one slept, came in, does, leaves, sleeps. */
export type DayItem =
  | {
      kind: 'stay';
      key: string;
      /** The night before and the night after in the same room: the day starts there; else the
       * day it is checked into, or out of. */
      edge: 'morning' | 'in' | 'out';
      /** Check-in or check-out on the local clock, when said; none for a morning. */
      at: string | null;
      place: DayPlace | null;
      booking: Booking;
      /** Which of its nights the day ends in, from 1. */
      night: number;
    }
  | {
      kind: 'arrive' | 'leave';
      key: string;
      /** When the way lands or leaves, on the local clock at the stop. */
      at: string | null;
      place: DayPlace | null;
      mode: Mode;
      /** The town it comes from or goes to. */
      other: string;
      /** The terminal it lands at or leaves from, as its ticket says. */
      terminal: string | null;
    }
  | {
      /** Back to the room slept in at the day's end: no card of its own, only the way there. */
      kind: 'back';
      key: string;
      at: null;
      place: DayPlace | null;
      booking: Booking;
      /** Which of its nights the day ends in, from 1. */
      night: number;
      /** The room's card at the day's start, picked when this is pressed. */
      morning: string;
    }
  | {
      kind: 'thing';
      key: string;
      at: string | null;
      place: DayPlace | null;
      item: AgendaItem;
      /** Its number on the map and the list: the day's things counted from 1. */
      number: number;
    };

/** The legs of the ticket bought for a stop's way on, if one is. */
function legsOf(stop: PlanStop) {
  return stop.bookings.find((b) => b.kind === 'ticket' && live(b))?.legs ?? [];
}

/** The days of a stop, its first to the one it leaves on. */
export function stopDays(stop: PlanStop): string[] {
  const days: string[] = [];
  for (let day = stop.start; day <= stop.end; day = plusDays(day, 1)) days.push(day);
  return days;
}

/** The stay whose room the night of `day` is spent in, if one is booked. */
function nightOf<T extends Booking & { day: string }>(
  stays: readonly T[],
  day: string,
): T | undefined {
  return stays.find((stay) => stay.day <= day && day < plusDays(stay.day, stay.nights));
}

/**
 * Where each falls in a day: the morning, a room left and the way in first, by their times; then
 * the things done in the order the person put them; then a room checked into, the way on and the
 * way back, by theirs — a check-out at eleven and a check-in at three when not said.
 */
function orderOf(item: DayItem, index: number): [number, string] {
  if (item.kind === 'thing') return [1, String(index).padStart(4, '0')];
  if (item.kind === 'stay') {
    const said = { morning: '00:00', out: '11:00', in: '15:00' }[item.edge];
    return [item.edge === 'in' ? 2 : 0, item.at ?? said];
  }
  if (item.kind === 'arrive') return [0, item.at ?? '00:01'];
  if (item.kind === 'leave') return [2, item.at ?? '23:59'];
  return [2, '24:00'];
}

/** What a day at a stop of a plan holds, in its order. */
export function dayItems(plan: Plan, stop: PlanStop, day: string): DayItem[] {
  const items: DayItem[] = [];
  const stays = stop.bookings.filter(
    (b): b is Booking & { day: string } => b.kind === 'stay' && live(b) && b.day !== null,
  );
  const slept = nightOf(stays, plusDays(day, -1));
  const sleeps = nightOf(stays, day);
  const stayed = (booking: Booking & { day: string }, edge: 'morning' | 'in' | 'out') => {
    const at = { morning: null, in: booking.checkIn, out: booking.checkOut }[edge];
    const night = Math.round((Date.parse(day) - Date.parse(booking.day)) / 86_400_000) + 1;
    items.push({
      kind: 'stay',
      key: `${booking.id}-${edge}`,
      edge,
      at,
      place: booking.place,
      booking,
      night,
    });
  };
  if (slept && sleeps === slept) {
    stayed(slept, 'morning');
    items.push({
      kind: 'back',
      key: `${slept.id}-back`,
      at: null,
      place: slept.place,
      booking: slept,
      night: Math.round((Date.parse(day) - Date.parse(slept.day)) / 86_400_000) + 1,
      morning: `${slept.id}-morning`,
    });
  } else {
    if (slept) stayed(slept, 'out');
    if (sleeps) stayed(sleeps, 'in');
  }
  // The way in lands the day the stop begins; the way on leaves the day it ends.
  const before = plan.stops[plan.stops.indexOf(stop) - 1];
  const came = before?.out;
  // When it lands and leaves: as the way says, else as its ticket does.
  const landed = before ? wayTimes(before).arrivalAt : null;
  const leaving = wayTimes(stop).departureAt;
  if (day === stop.start && before && came) {
    items.push({
      kind: 'arrive',
      key: 'arrive',
      at: landed ? wallOf(landed, stop.zone).time : null,
      place: came.to,
      mode: came.mode,
      other: before.city,
      terminal: legsOf(before).at(-1)?.terminalTo ?? null,
    });
  }
  const next = plan.stops[plan.stops.indexOf(stop) + 1];
  if (day === stop.end && stop.out) {
    items.push({
      kind: 'leave',
      key: 'leave',
      at: leaving ? wallOf(leaving, stop.zone).time : null,
      place: stop.out.from,
      mode: stop.out.mode,
      other: next ? next.city : '…',
      terminal: legsOf(stop)[0]?.terminalFrom ?? null,
    });
  }
  stop.agenda
    .filter((item) => item.day === day)
    .forEach((item, index) => {
      items.push({
        kind: 'thing',
        key: item.id,
        at: item.starts,
        place: item.place,
        item,
        number: index + 1,
      });
    });
  return (
    items
      .map((item, index) => ({ item, order: orderOf(item, index) }))
      // A sort that keeps the order of those alike: their kind's.
      .sort((a, b) => a.order[0] - b.order[0] || a.order[1].localeCompare(b.order[1]))
      .map(({ item }) => item)
  );
}

/** A step of a day from one place to the next: the two things, and where each is. */
export interface Step {
  from: DayItem;
  to: DayItem;
  start: DayPlace;
  end: DayPlace;
}

/** Each step of a day from one place to another, both known: a walk to ask the way of. */
export function stepsOf(items: readonly DayItem[]): Step[] {
  const steps: Step[] = [];
  items.forEach((to, index) => {
    const from = items[index - 1];
    // At the same place, there is no step to take: a room left for another in the house.
    const same = from?.place?.lat === to.place?.lat && from?.place?.lon === to.place?.lon;
    if (from?.place && to.place && !same) {
      steps.push({ from, to, start: from.place, end: to.place });
    }
  });
  return steps;
}

/** A thing as sent again, gone to another way: as it is kept, its price in its own money. */
export function agendaInputOf(
  stop: string,
  item: AgendaItem,
  currency: string,
  way: WaySaid,
): AgendaInput {
  const kept = item.paid ?? item.price;
  return {
    stop,
    name: item.name,
    day: item.day,
    starts: item.starts,
    ends: item.ends,
    place: item.place,
    price: kept?.minor ?? null,
    currency: kept?.currency ?? currency,
    booked: item.booked,
    link: item.link,
    ...way,
  };
}
